// 게임 상태 관리 클래스
class GameStateManager {
  constructor(game) {
    this.game = game;
    this.isPausedByTab = false; // 탭 전환으로 인한 자동 일시정지 여부
    this.isGameOver = false; // 게임 종료 상태
    this.gameOverStartTime = null; // 게임 종료 시작 시간
  }

  // CompanionManager 참조 설정
  setCompanionManager(companionManager) {
    if (this.game.companionManager) {
      this.game.companionManager.gameStateManager = this;
    }
  }

  // 게임 일시정지
  pause(fromTab = false) {
    if (this.game.gameLoop) {
      // 탭 전환이면 완전히 중지, 레벨업이면 일시정지 (카메라 회전 효과를 위해)
      if (fromTab) {
        this.game.gameLoop.stop();
        this.isPausedByTab = true;
      } else {
        this.game.gameLoop.pause();
      }

      // 게임 일시정지 시 타이머도 일시정지 (시간 유지)
      if (this.game.uiManager) {
        this.game.uiManager.stopGameTimer();
      }
      // 아이템 생성 중단
      if (this.game.itemManager) {
        this.game.itemManager.stopSpawning();
      }
      // 적 스폰 중단
      if (this.game.spawnScheduler) {
        this.game.spawnScheduler.stop();
      }
      // 조이스틱 숨김
      if (this.game.inputHandler && this.game.inputHandler.hideJoystick) {
        this.game.inputHandler.hideJoystick();
      }
    }
  }

  // 게임 재시작
  resume(fromTab = false) {
    // 탭 복귀로 인한 재개인데, 탭으로 일시정지된 게 아니면 무시
    if (fromTab && !this.isPausedByTab) {
      return;
    }

    if (this.game.gameLoop) {
      // 탭 전환이면 완전히 재시작, 레벨업이면 재개
      if (fromTab) {
        this.game.gameLoop.start();
        this.isPausedByTab = false;
      } else {
        this.game.gameLoop.resume();
      }

      // 게임 재시작 시 타이머도 재개 (이전 시간 유지)
      if (this.game.uiManager) {
        this.game.uiManager.resumeGameTimer();
      }
      // 아이템 생성 재개
      if (this.game.itemManager) {
        this.game.itemManager.resumeSpawning();
      }
      // 적 스폰 재개 - 레벨업 후에는 기존 스케줄을 유지하고 단순히 재개만 함
      if (this.game.spawnScheduler) {
        // 한국어 주석: 레벨업 후에는 게임 시간과 스테이지를 유지한 채로 재개만 함
        // initSpawns()는 게임 재시작 시에만 호출됨
        this.game.spawnScheduler.resume();
      }
    }
  }

  // 게임 종료 처리
  handleGameOver(attackerPosition = null) {
    if (this.isGameOver) return; // 이미 게임 종료 상태면 무시

    this.isGameOver = true;
    this.gameOverStartTime = Date.now();

    // 한국어 주석: 플레이어 사망 시 카메라 회전 연출 적용 (보스 대신 여기서 사용)
    try {
      const rotCfg = GameConfig.CAMERA?.ROTATION?.ON_PLAYER_DEATH;
      if (this.game.cameraController && rotCfg && rotCfg.ENABLED) {
        this.game.cameraController.startRotation(rotCfg.DURATION ?? 1.5);
      }
    } catch (_) {}

    // 1. 아이템 생성 중단 (기존 아이템은 유지)
    if (this.game.itemManager) {
      this.game.itemManager.stopSpawning();
    }

    // 2. 적 생성 중단
    this.game.coordinator.stopSpawnSystem();

    // 3. 플레이어 죽음 애니메이션 재생 (가장 먼저)
    if (this.game.player && this.game.player.playDeathAnimation) {
      this.game.player.playDeathAnimation(attackerPosition);

      // 죽음 애니메이션이 끝나면 시체 상태로 전환 (1.5초 후)
      setTimeout(() => {
        if (this.game.player && this.game.player.startCorpseState) {
          this.game.player.startCorpseState();
        }
      }, PlayerConfig.DEATH_ANIMATION_DURATION * 1000);
    }

    // 4. 적들을 모두 idle 상태로 변경, Companion들도 idle 상태로 변경
    this.setEnemiesIdle();
    this.setCompanionsIdle();

    // 5. 1초 후 Companion들의 앉기 애니메이션 시작
    setTimeout(() => {
      this.playCompanionsSit();
    }, PlayerConfig.DEATH_ANIMATION_DURATION * 1000);

    // 5. 2초 후 적들의 victory 애니메이션 시작
    setTimeout(() => {
      this.playEnemiesDance();
    }, PlayerConfig.DEATH_ANIMATION_DURATION * 1000 + 500);

    // 6. 5초 후 게임 종료 화면 표시
    setTimeout(() => {
      this.showGameOverScreen();
    }, PlayerConfig.DEATH_ANIMATION_DURATION * 1000 + 2000);
  }

  // 적들을 모두 idle 상태로 변경
  setEnemiesIdle() {
    if (!this.game.enemySpawnManager) return;

    this.game.enemySpawnManager.enemies.forEach((enemy) => {
      if (enemy.isAliveCheck()) {
        // 적이 살아있으면 idle 모션으로 강제 변경
        if (enemy.forceIdleState) {
          enemy.forceIdleState();
        }
      }
    });
  }

  // Companion들을 모두 idle 상태로 변경
  setCompanionsIdle() {
    if (!this.game.companionManager) return;

    this.game.companionManager.companions.forEach((companion) => {
      if (companion.isAliveCheck()) {
        // Companion이 살아있으면 idle 모션으로 강제 변경
        if (companion.forceIdleState) {
          companion.forceIdleState();
        }
      }
    });
  }

  // Companion들의 앉기 애니메이션 재생
  playCompanionsSit() {
    if (this.game.companionManager) {
      this.game.companionManager.companions.forEach((companion) => {
        if (companion.isAliveCheck()) {
          // Companion이 살아있으면 앉기 모션 재생
          if (companion.playSitAnimation) {
            companion.playSitAnimation();
          }
        }
      });
    }
  }

  // 적들의 victory 모션 재생
  playEnemiesDance() {
    // 적들의 victory 애니메이션 재생
    if (this.game.enemySpawnManager) {
      this.game.enemySpawnManager.enemies.forEach((enemy) => {
        if (enemy.isAliveCheck()) {
          // 적이 살아있으면 victory 모션 재생
          if (enemy.playVictoryAnimation) {
            enemy.playVictoryAnimation();
          }
        }
      });
    }
  }

  // 게임 종료 화면 표시
  showGameOverScreen() {
    if (!this.game.uiManager) return;

    // 플레이어 정보 수집
    const playerInfo = {
      survivalTime: this.game.uiManager.getGameTime(),
      killCount: this.game.player.getLevelInfo().killedSkeletons,
      level: this.game.player.getLevelInfo().level,
      points: this.game.player.points,
    };

    // UI 매니저를 통해 게임 종료 화면 표시
    this.game.uiManager.showGameOverScreen(playerInfo);
  }

  // 게임 재시작
  restartGame() {
    // 게임 종료 상태 리셋
    this.isGameOver = false;
    this.gameOverStartTime = null;

    // UI 매니저를 통해 게임 종료 화면 숨기기
    if (this.game.uiManager) {
      this.game.uiManager.hideGameOverScreen();
    }

    // 업그레이드 시스템 리셋
    if (this.game.upgradeManager) {
      this.game.upgradeManager.reset();
    }

    // 한국어 주석: 파티 버프 계수 초기화 (게임 재시작 시 모든 버프 제거)
    if (window.Party) {
      Party.partyBuffCoefficients = {
        ATTACK_DAMAGE: 0,
        ATTACK_RANGE: 0,
        ATTACK_COOLTIME: 0,
        MAX_HEALTH: 0,
        MOVE_SPEED: 0,
      };
    }

    // 한국어 주석: 적과 동료를 먼저 제거 후 플레이어 리셋 (참조 꼬임 방지)
    // 모든 적 제거
    this.game.coordinator.removeAllEnemies();

    // 모든 Companion 제거
    if (this.game.companionManager) {
      this.game.companionManager.removeAllCompanions();
    }

    // 플레이어 리셋 (적과 동료가 모두 제거된 후)
    this.resetPlayer();

    // 모든 아이템 제거 및 생성 재개
    if (this.game.itemManager) {
      this.game.itemManager.clearAllItems();
      this.game.itemManager.resumeSpawning();
    }

    // 게임 타이머 리셋 및 재시작
    if (this.game.uiManager) {
      this.game.uiManager.resetGameTimer();
      this.game.uiManager.startGameTimer(); // 타이머 다시 시작
    }

    // 한국어 주석: 카메라 위치를 플레이어 시작 위치로 즉시 리셋
    if (this.game.cameraController) {
      this.game.cameraController.reset();
    }

    // 스폰 시스템 재시작 (스테이지 1부터)
    this.game.coordinator.initSpawns();

    // 게임 루프 재시작
    this.resume();
  }

  // 플레이어 리셋
  resetPlayer() {
    if (!this.game.player) return;

    // 한국어 주석: 적 목록 초기화 (빈 배열로 설정)
    this.game.player.setEnemies([]);

    // 기본 스탯으로 리셋
    this.game.player.attackDamage = PlayerConfig.ATTACK_DAMAGE;
    this.game.player.attackRange = PlayerConfig.ATTACK_RANGE;
    this.game.player.attackAngle = PlayerConfig.ATTACK_ANGLE;
    this.game.player.attackCooltime = PlayerConfig.ATTACK_COOLTIME;
    this.game.player.maxHealth = PlayerConfig.MAX_HEALTH; // 최대 체력으로 리셋
    this.game.player.moveSpeed = PlayerConfig.MOVE_SPEED;

    this.game.player.health = this.game.player.maxHealth; // 현재 체력을 최대 체력으로 회복
    this.game.player.isAlive = true;
    this.game.player.isDying = false;
    this.game.player.isCorpse = false;
    this.game.player.isHit = false;
    this.game.player.points = PlayerConfig.STARTING_POINTS;
    this.game.player.level = GameConfig.LEVEL_SYSTEM.STARTING_LEVEL;
    this.game.player.killedSkeletons = 0;
    this.game.player.pointsToNextLevel =
      this.game.player.calculatePointsToNextLevel();

    // 플레이어 위치 리셋
    this.game.player.position.copy(
      VectorUtils.objectToVector3(PlayerConfig.STARTING_POSITION)
    );
    if (this.game.player.model) {
      this.game.player.model.position.copy(this.game.player.position);
    }

    // 검/방패 초기 상태로 리셋 (레벨 0 = 무기 없음)
    if (this.game.player.updateSword) {
      this.game.player.updateSword(0);
    }
    if (this.game.player.updateShield) {
      this.game.player.updateShield(0);
    }

    // 애니메이션 상태 완전 초기화
    this.game.player.resetAnimationState();

    // 한국어 주석: baseStats 초기화 (파티 버프 재적용을 위한 기준값 설정)
    if (this.game.player.updateBaseStats) {
      this.game.player.updateBaseStats();
    }

    // 체력바 재생성
    this.game.player.createHealthBar();

    // 한국어 주석: 기존 공격 범위 mesh 제거 후 재생성
    this.game.player.removeAttackVisual();
    this.game.player.createAttackVisual();
  }

  // 게임 종료 상태 확인
  isGameOverState() {
    return this.isGameOver;
  }
}

// webpack 빌드를 위해 default export 추가
export default GameStateManager;
